const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(res.status, error.error || 'Unknown error');
    }
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  private async uploadFile<T>(path: string, formData: FormData): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // No Content-Type header — browser sets multipart boundary automatically
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(res.status, error.error || 'Unknown error');
    }
    return res.json();
  }

  // Auth
  async register(data: { email: string; password: string; name: string; captcha_token?: string }): Promise<User> {
    return this.request('/api/auth/register', { method: 'POST', body: JSON.stringify(data) });
  }
  async login(data: { email: string; password: string; captcha_token?: string; totp_code?: string }): Promise<LoginResponse> {
    return this.request('/api/auth/login', { method: 'POST', body: JSON.stringify(data) });
  }
  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
  }
  async resendVerification(): Promise<{ message: string }> {
    return this.request('/api/auth/resend-verification', { method: 'POST' });
  }
  async setupTOTP(): Promise<TOTPSetupResponse> {
    return this.request('/api/auth/totp/setup', { method: 'POST' });
  }
  async verifyTOTP(code: string): Promise<{ message: string }> {
    return this.request('/api/auth/totp/verify', { method: 'POST', body: JSON.stringify({ code }) });
  }
  async disableTOTP(code: string): Promise<{ message: string }> {
    return this.request('/api/auth/totp/disable', { method: 'POST', body: JSON.stringify({ code }) });
  }
  async getConfig(): Promise<{ hcaptcha_site_key: string }> {
    return this.request('/api/config');
  }
  getGitHubIntegrationUrl() { return `${this.baseUrl}/api/auth/github`; }
  getGitLabIntegrationUrl() { return `${this.baseUrl}/api/auth/gitlab`; }
  getIntegrationUrl(provider: string) {
    switch (provider) {
      case 'gitlab': return this.getGitLabIntegrationUrl();
      default: return this.getGitHubIntegrationUrl();
    }
  }
  async getMe(): Promise<User> { return this.request('/api/auth/me'); }
  async logout(): Promise<void> { return this.request('/api/auth/logout', { method: 'POST' }); }

  // Integrations
  async getIntegrations(): Promise<Integration[]> { return this.request('/api/integrations'); }
  async disconnectIntegration(provider: string): Promise<void> { return this.request(`/api/integrations/${provider}`, { method: 'DELETE' }); }
  async getGitHubRepos(): Promise<GitHubRepo[]> { return this.request('/api/integrations/github/repos'); }
  async getGitLabRepos(): Promise<GitHubRepo[]> { return this.request('/api/integrations/gitlab/repos'); }
  getReposByProvider(provider: string): Promise<GitHubRepo[]> {
    switch (provider) {
      case 'gitlab': return this.getGitLabRepos();
      default: return this.getGitHubRepos();
    }
  }

  // Teams
  async getTeams(): Promise<Team[]> { return this.request('/api/teams'); }
  async getTeam(teamId: string): Promise<Team> { return this.request(`/api/teams/${teamId}`); }
  async createTeam(data: { name: string; app_ids?: string[] }): Promise<Team> { return this.request('/api/teams', { method: 'POST', body: JSON.stringify(data) }); }
  async updateTeam(teamId: string, data: { name?: string; auto_manage_signing?: boolean }): Promise<Team> { return this.request(`/api/teams/${teamId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteTeam(teamId: string): Promise<void> { return this.request(`/api/teams/${teamId}`, { method: 'DELETE' }); }
  async assignAppsToTeam(teamId: string, appIds: string[]): Promise<void> { return this.request(`/api/teams/${teamId}/apps`, { method: 'PUT', body: JSON.stringify({ app_ids: appIds }) }); }
  async getTeamMembers(teamId: string): Promise<TeamMemberDetail[]> { return this.request(`/api/teams/${teamId}/members`); }
  async inviteTeamMember(teamId: string, data: { email: string; role: string }): Promise<TeamMemberDetail> { return this.request(`/api/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify(data) }); }
  async updateTeamMemberRole(teamId: string, userId: string, data: { role: string }): Promise<void> { return this.request(`/api/teams/${teamId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async removeTeamMember(teamId: string, userId: string): Promise<void> { return this.request(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE' }); }

  // Apps
  async getApps(teamId: string): Promise<App[]> { return this.request(`/api/teams/${teamId}/apps`); }
  async createApp(teamId: string, data: CreateAppRequest): Promise<App> { return this.request(`/api/teams/${teamId}/apps`, { method: 'POST', body: JSON.stringify(data) }); }
  async getApp(teamId: string, appId: string): Promise<App> { return this.request(`/api/teams/${teamId}/apps/${appId}`); }
  async updateApp(teamId: string, appId: string, data: UpdateAppRequest): Promise<App> { return this.request(`/api/teams/${teamId}/apps/${appId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async deleteApp(teamId: string, appId: string): Promise<void> { return this.request(`/api/teams/${teamId}/apps/${appId}`, { method: 'DELETE' }); }

  // Fetch signing material from the Apple developer account
  async getRemoteProfiles(teamId: string): Promise<{ ok: boolean; message?: string; profiles?: RemoteProfile[] }> { return this.request(`/api/teams/${teamId}/codesigning/ios/profiles/remote`); }
  async importRemoteProfiles(teamId: string, profileIds: string[]): Promise<{ ok: boolean; message?: string; imported?: number }> { return this.request(`/api/teams/${teamId}/codesigning/ios/profiles/remote/import`, { method: 'POST', body: JSON.stringify({ profile_ids: profileIds }) }); }
  async generateCertificate(teamId: string): Promise<{ ok: boolean; message?: string; created?: boolean; name?: string }> { return this.request(`/api/teams/${teamId}/codesigning/ios/certificates/generate`, { method: 'POST' }); }
  async getRemoteCertificates(teamId: string): Promise<{ ok: boolean; message?: string; certificates?: RemoteCertificate[] }> { return this.request(`/api/teams/${teamId}/codesigning/ios/certificates/remote`); }

  // Automatic iOS signing
  async setupSigning(appId: string): Promise<{ ok: boolean; message?: string; result?: { certificate_name?: string; certificate_created: boolean; profile_name?: string; profile_created: boolean } }> { return this.request(`/api/apps/${appId}/signing/setup`, { method: 'POST' }); }

  // App build config
  async getAppConfig(appId: string): Promise<AppConfigResponse> { return this.request(`/api/apps/${appId}/config`); }
  async saveAppConfig(appId: string, config: string): Promise<void> { return this.request(`/api/apps/${appId}/config`, { method: 'PUT', body: JSON.stringify({ config }) }); }
  async discardAppConfig(appId: string): Promise<void> { return this.request(`/api/apps/${appId}/config`, { method: 'DELETE' }); }

  // Workflows
  async getWorkflows(appId: string): Promise<Workflow[]> { return this.request(`/api/apps/${appId}/workflows`); }
  async getWorkflow(appId: string, workflowId: string): Promise<Workflow> { return this.request(`/api/apps/${appId}/workflows/${workflowId}`); }
  async updateWorkflow(appId: string, workflowId: string, data: Partial<Workflow>): Promise<Workflow> { return this.request(`/api/apps/${appId}/workflows/${workflowId}`, { method: 'PATCH', body: JSON.stringify(data) }); }

  // Variable groups
  async getVariableGroups(appId: string): Promise<VariableGroup[]> { return this.request(`/api/apps/${appId}/variables`); }
  async getVariableGroup(appId: string, groupId: string): Promise<VariableGroup> { return this.request(`/api/apps/${appId}/variables/${groupId}`); }
  async createVariableGroup(appId: string, data: { name: string }): Promise<VariableGroup> { return this.request(`/api/apps/${appId}/variables`, { method: 'POST', body: JSON.stringify(data) }); }
  async deleteVariableGroup(appId: string, groupId: string): Promise<void> { return this.request(`/api/apps/${appId}/variables/${groupId}`, { method: 'DELETE' }); }
  async setVariable(appId: string, groupId: string, data: { key: string; value: string; is_secret: boolean }): Promise<Variable> { return this.request(`/api/apps/${appId}/variables/${groupId}/vars`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteVariable(appId: string, groupId: string, variableId: string): Promise<void> { return this.request(`/api/apps/${appId}/variables/${groupId}/vars/${variableId}`, { method: 'DELETE' }); }

  // Code signing — iOS Certificates
  async uploadIOSCertificate(teamId: string, formData: FormData): Promise<IOSCertificate> { return this.uploadFile(`/api/teams/${teamId}/codesigning/ios/certificates`, formData); }
  async getIOSCertificates(teamId: string): Promise<IOSCertificate[]> { return this.request(`/api/teams/${teamId}/codesigning/ios/certificates`); }
  async deleteIOSCertificate(teamId: string, certId: string): Promise<void> { return this.request(`/api/teams/${teamId}/codesigning/ios/certificates/${certId}`, { method: 'DELETE' }); }

  // Code signing — iOS Provisioning Profiles
  async uploadIOSProfile(teamId: string, formData: FormData): Promise<IOSProvisioningProfile> { return this.uploadFile(`/api/teams/${teamId}/codesigning/ios/profiles`, formData); }
  async getIOSProfiles(teamId: string): Promise<IOSProvisioningProfile[]> { return this.request(`/api/teams/${teamId}/codesigning/ios/profiles`); }
  async deleteIOSProfile(teamId: string, profileId: string): Promise<void> { return this.request(`/api/teams/${teamId}/codesigning/ios/profiles/${profileId}`, { method: 'DELETE' }); }

  // Code signing — Android Keystores
  async uploadAndroidKeystore(teamId: string, formData: FormData): Promise<AndroidKeystore> { return this.uploadFile(`/api/teams/${teamId}/codesigning/android/keystores`, formData); }
  async getAndroidKeystores(teamId: string): Promise<AndroidKeystore[]> { return this.request(`/api/teams/${teamId}/codesigning/android/keystores`); }
  async deleteAndroidKeystore(teamId: string, keystoreId: string): Promise<void> { return this.request(`/api/teams/${teamId}/codesigning/android/keystores/${keystoreId}`, { method: 'DELETE' }); }

  // Code signing — Google Play
  async uploadGooglePlayServiceAccount(teamId: string, formData: FormData): Promise<GooglePlayServiceAccount> { return this.uploadFile(`/api/teams/${teamId}/codesigning/google-play`, formData); }
  async getGooglePlayServiceAccounts(teamId: string): Promise<GooglePlayServiceAccount[]> { return this.request(`/api/teams/${teamId}/codesigning/google-play`); }
  async deleteGooglePlayServiceAccount(teamId: string, accountId: string): Promise<void> { return this.request(`/api/teams/${teamId}/codesigning/google-play/${accountId}`, { method: 'DELETE' }); }

  // Code signing — CSR generation + .cer upload
  async generateCSR(teamId: string, commonName?: string): Promise<CSRResponse> { return this.request(`/api/teams/${teamId}/codesigning/ios/certificates/generate-csr`, { method: 'POST', body: JSON.stringify({ common_name: commonName || 'Apple Distribution' }) }); }
  async uploadCER(teamId: string, certId: string, formData: FormData): Promise<IOSCertificate> { return this.uploadFile(`/api/teams/${teamId}/codesigning/ios/certificates/${certId}/upload-cer`, formData); }

  // Code signing — App Store Connect API Keys
  async uploadAppStoreConnectKey(teamId: string, formData: FormData): Promise<AppStoreConnectKey> { return this.uploadFile(`/api/teams/${teamId}/codesigning/appstore-connect`, formData); }
  async getAppStoreConnectKeys(teamId: string): Promise<AppStoreConnectKey[]> { return this.request(`/api/teams/${teamId}/codesigning/appstore-connect`); }
  async deleteAppStoreConnectKey(teamId: string, keyId: string): Promise<void> { return this.request(`/api/teams/${teamId}/codesigning/appstore-connect/${keyId}`, { method: 'DELETE' }); }

  // File editor (proxy to GitHub/GitLab)
  async getFileTree(appId: string, ref?: string, path?: string): Promise<FileTreeEntry[]> {
    const params = new URLSearchParams();
    if (ref) params.set('ref', ref);
    if (path) params.set('path', path);
    return this.request(`/api/apps/${appId}/files/tree?${params}`);
  }
  async getFileContent(appId: string, path: string, ref?: string): Promise<FileContentResponse> {
    const params = new URLSearchParams({ path });
    if (ref) params.set('ref', ref);
    return this.request(`/api/apps/${appId}/files/content?${params}`);
  }
  async updateFile(appId: string, data: { path: string; content: string; sha: string; message: string; branch: string }): Promise<void> {
    return this.request(`/api/apps/${appId}/files/content`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async getRepoBranches(appId: string): Promise<RepoBranch[]> {
    return this.request(`/api/apps/${appId}/files/branches`);
  }

  // Builds
  async getBuilds(teamId: string, limit?: number, offset?: number): Promise<BuildListResponse> { return this.request(`/api/teams/${teamId}/builds?limit=${limit || 20}&offset=${offset || 0}`); }
  async getAppBuilds(appId: string, limit?: number, offset?: number): Promise<BuildListResponse> { return this.request(`/api/apps/${appId}/builds?limit=${limit || 20}&offset=${offset || 0}`); }
  async triggerBuild(appId: string, data: TriggerBuildRequest): Promise<Build> { return this.request(`/api/apps/${appId}/builds`, { method: 'POST', body: JSON.stringify(data) }); }
  async getBuild(buildId: string): Promise<BuildDetailResponse> { return this.request(`/api/builds/${buildId}`); }
  async cancelBuild(buildId: string): Promise<void> { return this.request(`/api/builds/${buildId}/cancel`, { method: 'POST' }); }
  getArtifactDownloadUrl(buildId: string, artifactId: string) { return `${this.baseUrl}/api/builds/${buildId}/artifacts/${artifactId}/download`; }

  // Billing — user-scoped (aggregates all teams)
  async getBilling(): Promise<UsageSummary> { return this.request('/api/billing'); }
  async getUsage(limit?: number, offset?: number): Promise<UsageListResponse> { return this.request(`/api/usage?limit=${limit || 20}&offset=${offset || 0}`); }
  async getActivePlans(): Promise<BillingPlan[]> { return this.request('/api/plans'); }
  async changePlan(planId: string): Promise<void> { return this.request('/api/billing/change-plan', { method: 'POST', body: JSON.stringify({ plan_id: planId }) }); }
  async createCheckout(data: { plan_id?: string; amount_usd?: number }): Promise<CheckoutResponse> { return this.request('/api/billing/checkout', { method: 'POST', body: JSON.stringify(data) }); }
  async createBillingPortal(): Promise<CheckoutResponse> { return this.request('/api/billing/portal', { method: 'POST' }); }
  async getBalance(): Promise<UserBalance> { return this.request('/api/balance'); }
  async getBalanceTransactions(limit?: number, offset?: number): Promise<BalanceTransactionListResponse> { return this.request(`/api/balance/transactions?limit=${limit || 20}&offset=${offset || 0}`); }

  // Billing — team-scoped (legacy)
  async getTeamBilling(teamId: string): Promise<UsageSummary> { return this.request(`/api/teams/${teamId}/billing`); }
  async getTeamUsage(teamId: string, limit?: number, offset?: number): Promise<UsageListResponse> { return this.request(`/api/teams/${teamId}/usage?limit=${limit || 20}&offset=${offset || 0}`); }

  // Admin API
  async adminGetWorkers(): Promise<Worker[]> { return this.request('/api/admin/workers'); }
  async adminDeleteWorker(workerId: string): Promise<void> { return this.request(`/api/admin/workers/${workerId}`, { method: 'DELETE' }); }
  async adminGetTeams(limit?: number, offset?: number): Promise<{ teams: Team[]; total: number }> { return this.request(`/api/admin/teams?limit=${limit || 50}&offset=${offset || 0}`); }
  async adminGetTeam(teamId: string): Promise<{ team: Team; members: TeamMember[] }> { return this.request(`/api/admin/teams/${teamId}`); }
  async adminGetUsers(limit?: number, offset?: number): Promise<{ users: AdminUser[]; total: number }> { return this.request(`/api/admin/users?limit=${limit || 50}&offset=${offset || 0}`); }
  async adminUpdateUser(userId: string, data: { is_admin?: boolean }): Promise<void> { return this.request(`/api/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async adminGetBuilds(limit?: number, offset?: number): Promise<BuildListResponse> { return this.request(`/api/admin/builds?limit=${limit || 50}&offset=${offset || 0}`); }
  async adminGetUsageRecords(limit?: number, offset?: number): Promise<UsageListResponse> { return this.request(`/api/admin/usage/records?limit=${limit || 50}&offset=${offset || 0}`); }
  async adminGetPlans(): Promise<BillingPlan[]> { return this.request('/api/admin/plans'); }
  async adminCreatePlan(data: Partial<BillingPlan>): Promise<BillingPlan> { return this.request('/api/admin/plans', { method: 'POST', body: JSON.stringify(data) }); }
  async adminUpdatePlan(planId: string, data: Partial<BillingPlan>): Promise<BillingPlan> { return this.request(`/api/admin/plans/${planId}`, { method: 'PATCH', body: JSON.stringify(data) }); }

  // Status (public)
  async getStatus(): Promise<StatusResponse> { return this.request('/api/status'); }
  async submitReport(data: { email: string; category: string; subject: string; description: string }): Promise<{ id: string }> { return this.request('/api/status/report', { method: 'POST', body: JSON.stringify(data) }); }

  // Admin — Status & Incidents
  async adminGetComponents(): Promise<StatusComponent[]> { return this.request('/api/admin/components'); }
  async adminUpdateComponentStatus(componentId: string, data: { status: string; uptime_pct: number }): Promise<void> { return this.request(`/api/admin/components/${componentId}/status`, { method: 'POST', body: JSON.stringify(data) }); }
  async adminGetIncidents(): Promise<StatusIncident[]> { return this.request('/api/admin/incidents'); }
  async adminCreateIncident(data: { title: string; severity: string; message: string; component_ids: string[] }): Promise<{ id: string }> { return this.request('/api/admin/incidents', { method: 'POST', body: JSON.stringify(data) }); }
  async adminUpdateIncident(incidentId: string, data: { status: string; message: string }): Promise<void> { return this.request(`/api/admin/incidents/${incidentId}/updates`, { method: 'POST', body: JSON.stringify(data) }); }
  async adminDeleteIncident(incidentId: string): Promise<void> { return this.request(`/api/admin/incidents/${incidentId}`, { method: 'DELETE' }); }

  // Admin — Reports
  async adminGetReports(status?: string): Promise<StatusReport[]> { return this.request(`/api/admin/reports${status ? `?status=${status}` : ''}`); }
  async adminUpdateReport(reportId: string, data: { status: string; admin_notes: string }): Promise<void> { return this.request(`/api/admin/reports/${reportId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const api = new ApiClient(API_URL);
export default api;

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
  is_admin?: boolean;
  email_verified?: boolean;
  totp_enabled?: boolean;
}

export interface LoginResponse {
  requires_totp?: boolean;
  id?: string;
  email?: string;
  name?: string;
  avatar_url?: string;
  is_admin?: boolean;
  email_verified?: boolean;
  totp_enabled?: boolean;
}

export interface TOTPSetupResponse {
  secret: string;
  qr_code: string;
  otpauth: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: string;
  created_at: string;
}

export interface TeamMemberDetail {
  id: string;
  team_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  created_at: string;
  user_name: string;
  user_email: string;
  user_avatar_url: string;
}

export interface Worker {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'busy';
  machine_type: string;
  platform: string;
  arch: string;
  capabilities: string[];
  labels: string[];
  ip_address: string;
  last_ping_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingPlan {
  id: string;
  name: string;
  slug: string;
  billing_type: 'free' | 'monthly' | 'payg';
  included_minutes: number;
  price_per_minute_cents: number;
  price_per_minute_linux_cents: number;
  monthly_price_cents: number;
  max_concurrent_builds: number;
  max_team_members: number;
  is_active: boolean;
  stripe_price_id?: string;
  created_at: string;
  updated_at: string;
}

export interface UserBalance {
  id: string;
  user_id: string;
  balance_cents: number;
  created_at: string;
  updated_at: string;
}

export interface BalanceTransaction {
  id: string;
  user_id: string;
  amount_cents: number;
  balance_after: number;
  description: string;
  stripe_payment_id?: string;
  build_id?: string;
  created_at: string;
}

export interface BalanceTransactionListResponse {
  transactions: BalanceTransaction[];
  total: number;
}

export interface CheckoutResponse {
  url: string;
}

export interface UsageSummary {
  plan_name: string;
  plan_slug: string;
  billing_type: 'free' | 'monthly' | 'payg';
  minutes_used: number;
  minutes_limit: number;
  remaining: number;
  percent: number;
  period_start: string;
  period_end: string;
}

export interface UsageRecord {
  id: string;
  team_id: string;
  build_id: string;
  machine_type: string;
  minutes: number;
  cost_cents: number;
  recorded_at: string;
  created_at: string;
}

export interface UsageListResponse {
  records: UsageRecord[];
  total: number;
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  auto_manage_signing?: boolean;
}

export interface App {
  id: string;
  team_id: string;
  name: string;
  git_provider: string;
  repo_url: string;
  repo_owner: string;
  repo_name: string;
  default_branch: string;
  project_type: string;
  project_path: string;
  bundle_id: string;
  ignore_repo_config: boolean;
  android_keystore_id?: string | null;
  google_play_account_id?: string | null;
  build_config?: string | null;
  is_active: boolean;
  created_at: string;
  last_build_at?: string | null;
  last_build_status?: string | null;
}

export interface AppConfigResponse {
  config: string;
  source: 'local' | 'repo' | 'none';
  is_valid?: boolean;
}

export interface Workflow {
  id: string;
  app_id: string;
  name: string;
  instance_type: string;
  max_build_duration: number;
  environment: Record<string, unknown>;
  scripts: Record<string, unknown>;
  artifacts: unknown[];
  publishing: Record<string, unknown>;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface VariableGroup {
  id: string;
  team_id: string;
  name: string;
  variables: Variable[];
  created_at: string;
  updated_at: string;
}

export interface Variable {
  id: string;
  group_id: string;
  key: string;
  value: string;
  is_secret: boolean;
  created_at: string;
  updated_at: string;
}

export interface IOSCertificate {
  id: string;
  team_id: string;
  name: string;
  cert_type: string;
  serial_number?: string;
  fingerprint?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface IOSProvisioningProfile {
  id: string;
  team_id: string;
  name: string;
  profile_name?: string;
  bundle_id?: string;
  profile_type: string;
  certificate_match: boolean;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface RemoteProfile {
  id: string;
  name: string;
  bundle_id: string;
  profile_type: string;
  expires_at?: string;
  already_stored: boolean;
}

export interface RemoteCertificate {
  id: string;
  name: string;
  serial_number: string;
  expires_at?: string;
  usable: boolean;
  reason?: string;
}

export interface AndroidKeystore {
  id: string;
  team_id: string;
  name: string;
  key_alias: string;
  created_at: string;
  updated_at: string;
}

export interface GooglePlayServiceAccount {
  id: string;
  team_id: string;
  name: string;
  package_name: string;
  created_at: string;
  updated_at: string;
}

export interface AppStoreConnectKey {
  id: string;
  team_id: string;
  name: string;
  key_id: string;
  issuer_id: string;
  created_at: string;
  updated_at: string;
}

export interface CSRResponse {
  id: string;
  csr_pem: string;
}

export interface Build {
  id: string;
  app_id: string;
  workflow_id: string;
  worker_id: string | null;
  build_number: number;
  status: 'queued' | 'preparing' | 'building' | 'publishing' | 'completed' | 'failed' | 'cancelled';
  branch: string;
  commit_sha: string;
  commit_message: string;
  commit_author: string;
  trigger_type: string;
  triggered_by_user_id: string | null;
  triggered_by_name: string;
  triggered_by_avatar_url: string;
  started_at: string | null;
  finished_at: string | null;
  duration_secs: number | null;
  created_at: string;
}

export interface BuildStep {
  id: string;
  build_id: string;
  name: string;
  step_order: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at: string | null;
  finished_at: string | null;
  duration_secs: number | null;
}

export interface BuildArtifact {
  id: string;
  build_id: string;
  name: string;
  file_size: number;
  content_type: string;
}

export interface BuildListResponse {
  builds: Build[];
  total: number;
}

export interface BuildDetailResponse {
  build: Build;
  steps: BuildStep[];
  artifacts: BuildArtifact[];
}

export interface CreateAppRequest {
  name: string;
  git_provider: string;
  repo_url: string;
  project_type: string;
  project_path?: string;
  bundle_id?: string;
  ignore_repo_config?: boolean;
}

export interface UpdateAppRequest {
  name?: string;
  default_branch?: string;
  project_type?: string;
  project_path?: string;
  bundle_id?: string;
  ignore_repo_config?: boolean;
  android_keystore_id?: string;
  google_play_account_id?: string;
  is_active?: boolean;
}

export interface TriggerBuildRequest {
  workflow_id?: string;
  branch: string;
  /** Overrides the automatically resolved build number for this build only. */
  build_number?: number;
}

export interface Integration {
  provider: string;
  connected: boolean;
}

export interface GitHubRepo {
  id: number;
  full_name: string;
  name: string;
  owner: string;
  html_url: string;
  clone_url: string;
  default_branch: string;
  private: boolean;
  language: string;
  updated_at: string;
}

// Status page types
export interface ComponentDailyStatus {
  date: string;
  status: string;
  uptime_pct: number;
}

export interface StatusComponent {
  id: string;
  name: string;
  description: string;
  display_order: number;
  current_status: string;
  uptime_pct: number;
  history: ComponentDailyStatus[];
}

export interface StatusIncidentUpdate {
  id: string;
  status: string;
  message: string;
  created_at: string;
}

export interface StatusIncident {
  id: string;
  title: string;
  status: string;
  severity: string;
  created_at: string;
  resolved_at?: string;
  updates: StatusIncidentUpdate[];
}

export interface StatusReport {
  id: string;
  email: string;
  category: string;
  subject: string;
  description: string;
  status: string;
  admin_notes?: string;
  created_at: string;
  resolved_at?: string;
}

export interface StatusResponse {
  overall_status: string;
  components: StatusComponent[];
  incidents: StatusIncident[];
  live_health: Record<string, string>;
}

// File editor types
export interface FileTreeEntry {
  name: string;
  path: string;
  type: 'file' | 'dir';
  size?: number;
  sha?: string;
}

export interface FileContentResponse {
  path: string;
  content: string;
  sha: string;
  size: number;
  encoding: string;
}

export interface RepoBranch {
  name: string;
  default: boolean;
}
